import { useLoaderData, type MetaFunction } from "react-router";
import type { GetSourceSummaryShape } from "~/.server/source-summary";
import { Header } from "~/components/header";
import { episodeShortName } from "~/model/episode";
import { sourceSlug } from "~/model/source";
import { NavButton } from "~/pages/episode/footer";
import { userFromMatches } from "~/pages/with-user";
import type { loader } from "./.server/loader";
import { SourceHeader } from "./header";
import { SourceSummarySection } from "./section";
import { Subscribe } from "./subscribe";

export { action } from "./.server/action";
export { loader } from "./.server/loader";

// FOOTER

function SourceFooter() {
  const { sourceSummary, previousEpisodeKey, nextEpisodeKey, indexEpisodeKey } = useLoaderData<typeof loader>();
  const slug = sourceSlug(sourceSummary.source);
  const nextEpisodeLink = nextEpisodeKey
    ? nextEpisodeKey === indexEpisodeKey
      ? `${slug}`
      : `${slug}/${nextEpisodeKey}`
    : undefined;
  return (
    <div className="flex flex-row items-start px-8 py-6 justify-between relative">
      <div className="grow basis-1 shrink-0 flex flex-row">
        <NavButton type="prev" link={previousEpisodeKey && `${slug}/${previousEpisodeKey}`} />
        <NavButton type="next" link={nextEpisodeLink} />
      </div>
      <div className="grow basis-1 my-1">
        <Subscribe />
      </div>
      <div className="grow basis-1" />
    </div>
  );
}

// SOURCE SUMMARY CONTENT

export function SourceSummaryContent({
  sourceSummary,
  showFullName,
}: {
  sourceSummary: GetSourceSummaryShape;
  showFullName: boolean;
}) {
  const viewMode = sourceSummary.source.config.viewMode ?? "list";

  return (
    <div className="w-full">
      <div className="content-w flex flex-col">
        <div className="mt-6 mx-6 text-[15px]">
          <SourceHeader sourceSummary={sourceSummary} showFullName={showFullName} />
          {sourceSummary.summaries.map((summarySection, index) => (
            <SourceSummarySection key={index} summarySection={summarySection} viewMode={viewMode} />
          ))}
        </div>
        <SourceFooter />
      </div>
    </div>
  );
}

// PAGE

export default function SourcePage() {
  const { sourceSummary, isMultipleSources } = useLoaderData<typeof loader>();
  return (
    <div className="flex flex-col min-h-full">
      <Header />
      {sourceSummary ? <SourceSummaryContent sourceSummary={sourceSummary} showFullName={isMultipleSources} /> : null}
    </div>
  );
}

// META

export const meta: MetaFunction = ({ loaderData, matches }) => {
  const { sourceSummary } = loaderData as ReturnType<typeof useLoaderData<typeof loader>>;
  if (!sourceSummary) return [{ title: "Sumcast" }];
  const user = userFromMatches(matches);
  return [{ title: `${sourceSummary.source.sourceName} · ${episodeShortName(sourceSummary.episode, user)} · Sumcast` }];
};
